import React from 'react';
import { Modal, Box, Typography, Button } from "@mui/material";

const RebalanceConfirmationModal = ({ 
  open, 
  onClose, 
  isFullscreen, 
  rebalanceData, 
  onConfirm,
  setIsFullscreen
}) => {
  const confirmmodalStyle = {
    position: "absolute",
    ...(isFullscreen ? {
      top: 0,
      left: 0,
      transform: 'none',
      width: '100%',
      height: '100%',
      maxWidth: 'none',
    } : {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "1200px",
    }),
    bgcolor: "background.paper",
    borderRadius: isFullscreen ? 0 : "4px",
    p: 0,
    outline: "none",
  };

  const headerStyle = {
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const closeButtonStyle = {
    padding: "8px",
    minWidth: "auto",
    color: "#666",
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#333',
    }
  };

  const tableStyles = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: 'fixed',
  };

  const tableHeaderStyles = {
    padding: "12px 24px",
    textAlign: "center",
    // Use inset box-shadow instead of border to avoid GPU tear/flicker on fast scroll
    boxShadow: "inset 0 -1px 0 #e0e0e0, 0 2px 4px rgba(0,0,0,0.1)",
    color: "#333",
    fontWeight: "normal",
    whiteSpace: "nowrap",
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: '#fff',
  };

  const tableCellStyles = {
    padding: "12px 24px",
    textAlign: "center",
    // Use inset box-shadow instead of border to avoid GPU tear/flicker on fast scroll
    boxShadow: "inset 0 -1px 0 #e0e0e0",
    // whiteSpace: 'nowrap',
    overflow: 'hidden',
    // textOverflow: 'ellipsis',
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: '#fff',
  };

  const tableContainerStyle = {
    maxHeight: isFullscreen ? "calc(100vh - 120px)" : "400px",
    overflow: "auto",
    position: 'relative',
    // Promote to its own compositing layer to reduce paint tearing while scrolling
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'transform',
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  };
  

  // Group rows by investor to ensure rowSpan applies only to contiguous rows
  const groupedRows = React.useMemo(() => {
    if (!Array.isArray(rebalanceData)) return [];
    const investorToRowsMap = new Map();
    rebalanceData.forEach((row, index) => {
      // Use uniqueInvestorId if available, otherwise fall back to investor name
      const investorKey = row?.uniqueInvestorId || row?.investor || '';
      if (!investorToRowsMap.has(investorKey)) {
        investorToRowsMap.set(investorKey, []);
      }
      investorToRowsMap.get(investorKey).push({ ...row, originalIndex: index });
    });

    const result = [];
    investorToRowsMap.forEach((rows, investorKey) => {
      rows.forEach((row, idx) => {
        result.push({
          ...row,
          __isFirstInvestorRow: idx === 0,
          __investorRowSpan: rows.length,
        });
      });
    });
    return result;
  }, [rebalanceData]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={confirmmodalStyle}>
        <Box sx={headerStyle}>
          <Typography variant="h6" style={{ fontSize: '18px', fontWeight: 500 }}>
            Confirm Rebalance
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button 
              onClick={() => setIsFullscreen(!isFullscreen)}
              sx={{
                minWidth: 'auto',
                p: 1,
                color: '#666',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#333',
                }
              }}
            >
              {isFullscreen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 3.41L20.59 2 14 8.59V5h-2v7h7v-2h-3.59L22 3.41zM3.41 22l1.41 1.41 6.59-6.59V20h2v-7h-7v2h3.59L3.41 22z"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              )}
            </Button>
            <Button 
              onClick={onClose}
              sx={closeButtonStyle}
            >
              ×
            </Button>
          </Box>
        </Box>

        <Box sx={tableContainerStyle}>
          <table style={tableStyles}>
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 2 }}>
              <tr>
                <th style={{
                  ...tableHeaderStyles,
                  boxShadow: "inset 0 -1px 0 #e0e0e0, 0 2px 4px rgba(0,0,0,0.1)"
                }}>Investor</th>
                <th style={tableHeaderStyles}>Asset</th>
                <th style={tableHeaderStyles}>Share Count</th>
                <th style={tableHeaderStyles}>Sell Quantity</th>
                <th style={tableHeaderStyles}>Days Held</th>
                <th style={tableHeaderStyles}>Cost Price</th>
                <th style={tableHeaderStyles}>Market Price</th>
                <th style={tableHeaderStyles}>Total Profit (Loss)</th>
              </tr>
            </thead>
            <tbody>
              {groupedRows.map((row, index) => {
                const isFirstInvestorRow = row.__isFirstInvestorRow;
                const investorRowSpan = row.__investorRowSpan;
                return (
                  <tr key={index} >
                    {isFirstInvestorRow && (
                      <td 
                        style={{
                          ...tableCellStyles,
                          fontWeight: 400,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1), inset 0 -1px 0 #e0e0e0'
                        }}
                        rowSpan={investorRowSpan}
                      >
                        {row.investor}
                      </td>
                    )}
                    <td style={tableCellStyles}>{row.symbol}</td>
                    <td style={tableCellStyles}>
                      {!isNaN(Number(row.shareCount)) && row.shareCount !== null && row.shareCount !== undefined 
                        ? Number(row.shareCount).toFixed(3) 
                        : '-'}
                    </td>
                    <td style={tableCellStyles}>
                      {!isNaN(Number(row.sellQuantity)) && row.sellQuantity !== null && row.sellQuantity !== undefined 
                        ? Number(row.sellQuantity).toFixed(3) 
                        : '-'}
                    </td>
                    <td style={tableCellStyles}>{row.daysHeld}</td>
                    <td style={tableCellStyles}>
                      {!isNaN(Number(row.costPrice)) && row.costPrice !== null && row.costPrice !== undefined 
                        ? `$${Number(row.costPrice).toFixed(3)}` 
                        : '-'}
                    </td>
                    <td style={tableCellStyles}>
                      {!isNaN(Number(row.marketPrice)) && row.marketPrice !== null && row.marketPrice !== undefined 
                        ? `$${Number(row.marketPrice).toFixed(3)}` 
                        : '-'}
                    </td>
                    <td style={{
                      ...tableCellStyles,
                      color: parseFloat(row.totalProfit) >= 0 ? '#2E7D32' : '#D32F2F'
                    }}>
                      ${Math.abs(row.totalProfit)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        <Box sx={{ 
          p: 2, 
          borderTop: "1px solid #e0e0e0", 
          display: "flex", 
          justifyContent: "flex-end", 
          gap: 1,
          backgroundColor: '#fff',
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              backgroundColor: '#1976d2',
            }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RebalanceConfirmationModal; 